/**
 * BB Defend vs Position — three-way (call / 3-bet / fold) decision
 * when an open folds around to the Big Blind.
 *
 * Per sheet 1: defend ~40% vs BTN, ~30% vs CO, ~20% vs UTG. 3-bet
 * range stays narrow for value (QQ+, AK) regardless of opener; the
 * calling range widens against later opens. Borderline hands (AJo,
 * KQo, ATo) flip from call vs late to fold vs early — exactly the
 * spots a recreational player gets wrong.
 *
 * Scenarios are curated rather than procedural — sheet 1 says "defend
 * by feel" and sheet 15 says "calling and 3-betting ranges by feel,"
 * so we lean on hand-picked unambiguous cases instead of trying to
 * formalize a fuzzy strategy. ~30 hands cover the core patterns.
 */

import { pickFrom } from "@/features/quiz/lib/common";

export type Action = "call" | "3bet" | "fold";
export type OpenerBucket = "early" | "middle" | "late";

export interface BbDefendScenario {
  hand: string; // standard notation: "AKs" / "77" / "AKo"
  vsEarly: Action;
  vsMiddle: Action;
  vsLate: Action;
}

/** Curated scenarios — clear cases first, opener-dependent cases at the
 *  end. If sheet 1 / sheet 15 are updated, this list updates with them. */
const SCENARIOS: BbDefendScenario[] = [
  // Premium pairs + AK — always 3-bet for value
  { hand: "AA", vsEarly: "3bet", vsMiddle: "3bet", vsLate: "3bet" },
  { hand: "KK", vsEarly: "3bet", vsMiddle: "3bet", vsLate: "3bet" },
  { hand: "QQ", vsEarly: "3bet", vsMiddle: "3bet", vsLate: "3bet" },
  { hand: "AKs", vsEarly: "3bet", vsMiddle: "3bet", vsLate: "3bet" },
  { hand: "AKo", vsEarly: "3bet", vsMiddle: "3bet", vsLate: "3bet" },

  // Strong but call-not-3bet for set-mining + flatting value
  { hand: "JJ", vsEarly: "call", vsMiddle: "call", vsLate: "call" },
  { hand: "TT", vsEarly: "call", vsMiddle: "call", vsLate: "call" },
  { hand: "99", vsEarly: "call", vsMiddle: "call", vsLate: "call" },
  { hand: "AQs", vsEarly: "call", vsMiddle: "call", vsLate: "call" },
  { hand: "AQo", vsEarly: "call", vsMiddle: "call", vsLate: "call" },

  // Small/medium pairs — call vs any open (set-mining at a discount)
  { hand: "77", vsEarly: "call", vsMiddle: "call", vsLate: "call" },
  { hand: "55", vsEarly: "call", vsMiddle: "call", vsLate: "call" },
  { hand: "22", vsEarly: "call", vsMiddle: "call", vsLate: "call" },

  // Suited broadways + suited aces — playable from BB at discount
  { hand: "AJs", vsEarly: "call", vsMiddle: "call", vsLate: "call" },
  { hand: "ATs", vsEarly: "call", vsMiddle: "call", vsLate: "call" },
  { hand: "KQs", vsEarly: "call", vsMiddle: "call", vsLate: "call" },
  { hand: "QJs", vsEarly: "call", vsMiddle: "call", vsLate: "call" },
  { hand: "JTs", vsEarly: "call", vsMiddle: "call", vsLate: "call" },

  // Suited connectors — call most opens; tightest vs UTG
  { hand: "98s", vsEarly: "call", vsMiddle: "call", vsLate: "call" },
  { hand: "87s", vsEarly: "call", vsMiddle: "call", vsLate: "call" },

  // Suited aces low — call for blocker + flush playability
  { hand: "A5s", vsEarly: "call", vsMiddle: "call", vsLate: "call" },

  // Borderline hands — fold vs early, call vs late (the LESSON)
  { hand: "AJo", vsEarly: "fold", vsMiddle: "call", vsLate: "call" },
  { hand: "KQo", vsEarly: "fold", vsMiddle: "call", vsLate: "call" },
  { hand: "ATo", vsEarly: "fold", vsMiddle: "fold", vsLate: "call" },
  { hand: "65s", vsEarly: "fold", vsMiddle: "call", vsLate: "call" },
  { hand: "86s", vsEarly: "fold", vsMiddle: "call", vsLate: "call" },

  // Trash — fold vs any open
  { hand: "72o", vsEarly: "fold", vsMiddle: "fold", vsLate: "fold" },
  { hand: "K3o", vsEarly: "fold", vsMiddle: "fold", vsLate: "fold" },
  { hand: "Q4o", vsEarly: "fold", vsMiddle: "fold", vsLate: "fold" },
  { hand: "J8o", vsEarly: "fold", vsMiddle: "fold", vsLate: "fold" },
  { hand: "53o", vsEarly: "fold", vsMiddle: "fold", vsLate: "fold" },
];

const OPENER_BUCKETS: OpenerBucket[] = ["early", "middle", "late"];

/** Specific opener position to show in the question prose, drawn from
 *  the bucket. UTG/CO/BTN are the canonical reps and produce the
 *  cleanest reads. */
const REP_POSITION: Record<OpenerBucket, string> = {
  early: "UTG",
  middle: "CO",
  late: "BTN",
};

export interface BbDefendQuestion {
  hand: string;
  openerBucket: OpenerBucket;
  /** Display position name for the prose ("UTG" / "CO" / "BTN"). */
  openerPosition: string;
  action: Action;
}

export function generateQuestion(
  rand: () => number = Math.random,
): BbDefendQuestion {
  const scenario = pickFrom(SCENARIOS, rand);
  const bucket = pickFrom(OPENER_BUCKETS, rand);
  const action =
    bucket === "early" ? scenario.vsEarly : bucket === "middle" ? scenario.vsMiddle : scenario.vsLate;
  return {
    hand: scenario.hand,
    openerBucket: bucket,
    openerPosition: REP_POSITION[bucket],
    action,
  };
}

/** Internals for testing — the curated table itself. */
export const _internals = { SCENARIOS, OPENER_BUCKETS, REP_POSITION };
